import { useAuthStatus } from '@/hooks';
import { handleUnauthorized } from '@/lib/shared/auth-shared';
import styles from '@/pages/Unauthorized/styles.module.css';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import Button from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/unauthorized/')({
  component: UnauthorizedPage,
});

function UnauthorizedPage() {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const { status } = useAuthStatus();

  const { referrerPath: storedReferrerPath, clearReferrerPath } = useUnauthorizedReferrerStore();

  // Capture the referrer path on mount then immediately clear it via the store
  // so both Zustand state and sessionStorage stay in sync.
  const [referrerPath] = useState<string | null>(storedReferrerPath);

  useEffect(() => {
    clearReferrerPath();
  }, [clearReferrerPath]);

  // Defensive guard: if the auth query transitions to UNAUTHENTICATED while
  // the user is sitting on /unauthorized (e.g. a background /auth/me 401 that
  // lands just after a race-induced navigation here), delegate to the global
  // unauthorized handler so the user is sent to /login with a clean session.
  useEffect(() => {
    if (status === 'UNAUTHENTICATED') {
      handleUnauthorized();
    }
  }, [status]);

  const handleGoHome = useCallback(() => {
    void navigate({ to: '/studies' });
  }, [navigate]);

  return (
    <Stack
      alignItems="center"
      className={styles.unauthorizedContainer}
      fullWidth
      fullHeight
      gap={4}
      justifyContent="center"
      orientation="vertical"
    >
      <Text as="h1" className={styles.unauthorizedTitle}>
        {t('unauthorized.title')}
      </Text>

      <Text as="span" className={styles.unauthorizedMessage}>
        {t('unauthorized.message1')}
      </Text>

      <Text as="span" className={styles.unauthorizedMessage}>
        {t('unauthorized.message2')}
      </Text>

      {referrerPath && (
        <Text as="span" className={styles.unauthorizedMessage} data-testid="attempted-path">
          {t('unauthorized.attempted_path', { path: referrerPath, interpolation: { escapeValue: false } })}
        </Text>
      )}

      <Button
        aria-label={t('unauthorized.go_home')}
        data-testid={'go-to-home-btn'}
        onPress={handleGoHome}
        size="md"
        visual="primary"
      >
        {t('unauthorized.go_home')}
      </Button>
    </Stack>
  );
}
